import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { CheckIcon, XIcon } from 'lucide-react';
import { useAuth } from '../hooks';
import { ROUTES } from '../routes';
import { ApiError } from '../utils/apiClient';
import { Cursor, MockdLogo } from '../components/common';

type Status = 'confirming' | 'success' | 'wrong-account' | 'error' | 'retry';

// Landing page for the link PUT /api/auth/me/email mails to the new address
// (#263). The change only applies while signed in to the account that asked
// for it, so a signed-out visitor is sent to sign in and brought back here.
export function ConfirmEmailChange() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading, confirmEmailChange, logout } = useAuth();

  const token = searchParams.get('token');
  const [status, setStatus] = useState<Status>('confirming');
  const [error, setError] = useState<string | null>(null);
  const attempted = useRef(false);

  const runConfirm = useCallback((token: string) => {
    setStatus('confirming');
    confirmEmailChange(token)
      .then(() => setStatus('success'))
      .catch((err) => {
        // A network failure or server error says nothing about the link, which
        // may still work — offer a retry rather than calling it invalid.
        if (!(err instanceof ApiError) || err.status >= 500) {
          setStatus('retry');
          return;
        }
        setError(err.message);
        setStatus(err.code === 'WRONG_ACCOUNT' ? 'wrong-account' : 'error');
      });
  }, [confirmEmailChange]);

  const signedIn = Boolean(user);
  useEffect(() => {
    if (!token || loading || !signedIn || attempted.current) return;
    attempted.current = true;
    runConfirm(token);
  }, [token, loading, signedIn, runConfirm]);

  // Signing in carries this path through magic_link_tokens.redirect_path or
  // oauth_states.redirect_path, so the token sits there in plain text for a few
  // minutes. That's acceptable: it's single-use, only works with the requesting
  // account's session, and those rows are short-lived. Riding along with the
  // sign-in is what lets a magic link opened in another browser still finish.
  const returnPath = token ? `${ROUTES.confirmEmail()}?token=${encodeURIComponent(token)}` : ROUTES.account();
  const signInUrl = `${ROUTES.auth.login()}?${new URLSearchParams({ redirect: returnPath })}`;

  const handleSwitchAccount = async () => {
    await logout();
    navigate(signInUrl);
  };

  if (!token) {
    return (
      <Frame>
        <FailureIcon />
        <h1 className="text-base-content mb-2">Invalid Link</h1>
        <p className="text-base-content/80 mb-6">This confirmation link is invalid.</p>
        <Link to={ROUTES.account()} className="btn btn-primary w-full">
          Go to account settings
        </Link>
      </Frame>
    );
  }

  if (!loading && !user) {
    return (
      <Frame>
        <h1 className="text-base-content mb-2">Sign in to confirm</h1>
        <p className="text-base-content/80 mb-6">
          Sign in to the account that asked for this change, and we&apos;ll finish it.
        </p>
        <Link to={signInUrl} className="btn btn-primary w-full">
          Sign in
        </Link>
      </Frame>
    );
  }

  return (
    <Frame>
      {status === 'confirming' && (
        <>
          <Cursor size="lg" className="mx-auto mb-4" />
          <h1 className="text-base-content mb-2">Confirming</h1>
          <p className="text-base-content/80">Please wait while we update your email...</p>
        </>
      )}

      {status === 'success' && (
        <>
          <div className="w-12 h-12 mx-auto mb-4 bg-success/20 rounded-full flex items-center justify-center">
            <CheckIcon className="size-6 text-success" />
          </div>
          <h1 className="text-base-content mb-2">Email changed</h1>
          <p className="text-base-content/80 mb-6">
            Your account email is now <span className="font-medium break-all">{user?.email}</span>.
          </p>
          <Link to={ROUTES.account()} className="btn btn-primary w-full">
            Go to account settings
          </Link>
        </>
      )}

      {status === 'wrong-account' && (
        <>
          <FailureIcon />
          <h1 className="text-base-content mb-2">Different account</h1>
          <p className="text-base-content/80 mb-6">
            You&apos;re signed in as <span className="font-medium break-all">{user?.email}</span>, but this
            link is for another mockd account. Sign in to that account to confirm the change.
          </p>
          <button className="btn btn-primary w-full" onClick={handleSwitchAccount}>
            Sign in to a different account
          </button>
        </>
      )}

      {status === 'retry' && (
        <>
          <FailureIcon />
          <h1 className="text-base-content mb-2">Couldn&apos;t reach mockd</h1>
          <p className="text-base-content/80 mb-6">
            Something went wrong confirming your email. Your link may still work.
          </p>
          <button className="btn btn-primary w-full" onClick={() => runConfirm(token)}>
            Try again
          </button>
        </>
      )}

      {status === 'error' && (
        <>
          <FailureIcon />
          <h1 className="text-base-content mb-2">Couldn&apos;t change your email</h1>
          <p className="text-base-content/80 mb-6">{error || 'This link is invalid or has expired.'}</p>
          <Link to={ROUTES.account()} className="btn btn-primary w-full">
            Go to account settings
          </Link>
        </>
      )}
    </Frame>
  );
}

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex-1 bg-base-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="mb-8">
          <MockdLogo size="md" className="mx-auto" />
        </div>
        <div className="card bg-base-100 p-6">{children}</div>
      </div>
    </div>
  );
}

function FailureIcon() {
  return (
    <div className="w-12 h-12 mx-auto mb-4 bg-error/20 rounded-full flex items-center justify-center">
      <XIcon className="size-6 text-error" />
    </div>
  );
}
